/**
 * Authentication hook.
 *
 * Provides convenient access to auth state and actions:
 * - user: Current authenticated user
 * - status: Authentication status
 * - isAuthenticated: Boolean helper
 * - login: Login action
 *
 * Automatically checks auth status on initial mount.
 * Handles 401 errors by clearing auth state.
 */

import { useEffect } from 'react';

import type { User } from '@/api/auth';
import { useAuthStore, selectIsAuthenticated, type AuthStatus } from '@/store/auth';

function shouldRunInitialAuthCheck(
  status: AuthStatus,
  options: { skipInitialCheck: boolean },
): boolean {
  return !options.skipInitialCheck && status === 'idle';
}

/**
 * Return type for useAuth hook.
 */
export interface UseAuthReturn {
  /**
   * Current authenticated user, null if not authenticated.
   */
  user: User | null;

  /**
   * Current authentication status.
   */
  status: AuthStatus;

  /**
   * True if user is authenticated.
   */
  isAuthenticated: boolean;

  /**
   * Login with username (email) and password.
   * Throws ApiError on failure.
   */
  login: (username: string, password: string) => Promise<User | null>;

  /**
   * Re-check authentication status.
   */
  checkAuth: () => Promise<void>;
}

/**
 * Authentication hook with automatic session check.
 *
 * @param options.skipInitialCheck - Skip automatic auth check on mount (default: false)
 * @returns Auth state and actions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isAuthenticated, login } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <LoginForm onLogin={login} />;
 *   }
 *
 *   return <div>Welcome, {user?.display_name ?? user?.username}</div>;
 * }
 * ```
 */
export function useAuth(options?: { skipInitialCheck?: boolean }): UseAuthReturn {
  const { skipInitialCheck = false } = options ?? {};

  const user = useAuthStore((state) => state.user);
  const status = useAuthStore((state) => state.status);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const storeLogin = useAuthStore((state) => state.login);
  const storeCheckAuth = useAuthStore((state) => state.checkAuth);
  const shouldCheckInitialAuth = shouldRunInitialAuthCheck(status, { skipInitialCheck });

  // Check auth on mount if not already checked
  useEffect(() => {
    if (!shouldCheckInitialAuth) return;
    void storeCheckAuth();
  }, [shouldCheckInitialAuth, storeCheckAuth]);

  return {
    user,
    status,
    isAuthenticated,
    login: storeLogin,
    checkAuth: storeCheckAuth,
  };
}
